import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAdminContext } from './context';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

export function AdminLayout({ children }) {
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [collapsed, setCollapsed] = useState(false);
  const context = useAdminContext();

  useEffect(() => {
    setCollapsed(isSmallScreen);
  }, [isSmallScreen]);

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {buildSidebar()}
      <Box sx={{ flexGrow: 1, height: '100%', overflow: 'auto' }}>
        {children}
      </Box>
    </Box>
  );

  function withTooltip(content, label) {
    if (!collapsed) return content;

    return (
      <Tooltip
        title={label}
        placement="right"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -24],
                },
              },
            ],
          },
        }}
      >
        {content}
      </Tooltip>
    );
  }

  function buildSidebar() {
    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

    return (
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            position: 'relative',
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Box
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            pl: 2,
            pr: collapsed ? 2 : 0.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            gap: 1,
            height: 48,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
            },
          }}
        >
          {collapsed ? (
            <ChevronRightIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: '1.5rem',
              }}
            />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '1.5rem',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Admin Panel
                </Typography>
              </Box>
              <ChevronLeftIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '1.5rem',
                }}
              />
            </>
          )}
        </Box>
        <List sx={{ pt: 0 }}>{context.routes.map(buildLink)}</List>
      </Drawer>
    );
  }

  function buildLink(route, i) {
    const hasChildren = route.children?.length;
    const IconComponent = route.icon;

    const isParentActive = hasChildren
      ? isParentRouteActive(route)
      : isActiveRoute(route);

    if (collapsed && hasChildren) {
      return route.children.map((child) => (
        <ListItem key={child.path} disablePadding>
          {withTooltip(
            <ListItemButton
              component={Link}
              to={child.path}
              selected={location.pathname === `/admin${child.path}`}
              sx={{
                justifyContent: 'center',
                px: 0,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              {IconComponent && (
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === `/admin${child.path}`
                        ? 'primary.contrastText'
                        : 'inherit',
                    minWidth: 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent />
                </ListItemIcon>
              )}
            </ListItemButton>,
            child.label,
          )}
        </ListItem>
      ));
    }

    const button = (
      <ListItemButton
        component={hasChildren ? 'div' : Link}
        to={hasChildren ? undefined : `/admin${route.path}`}
        selected={isParentActive && !hasChildren}
        disabled={hasChildren}
        sx={{
          cursor: hasChildren ? 'default' : 'pointer',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 2,
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          },
          '&.Mui-disabled': {
            opacity: 1,
          },
        }}
      >
        {IconComponent && (
          <ListItemIcon
            sx={{
              color:
                isParentActive && !hasChildren
                  ? 'primary.contrastText'
                  : 'inherit',
              minWidth: collapsed ? 'auto' : 36,
              justifyContent: 'center',
            }}
          >
            <IconComponent />
          </ListItemIcon>
        )}
        {!collapsed && <ListItemText primary={route.label} />}
      </ListItemButton>
    );

    return (
      <Box key={i}>
        <ListItem disablePadding>{withTooltip(button, route.label)}</ListItem>
        {hasChildren && !collapsed ? buildChildRoutes(route) : null}
      </Box>
    );
  }

  function buildChildRoutes(route) {
    return (
      <List sx={{ py: 0, mt: -1 }}>
        {route.children.map((child) => (
          <ListItem key={child.path} disablePadding>
            <ListItemButton
              component={Link}
              to={`/admin${child.path}`}
              selected={location.pathname === `/admin${child.path}`}
              sx={{
                pl: 7,
                py: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              <ListItemText
                primary={child.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }

  function isActiveRoute(route) {
    return route.exact
      ? location.pathname === `/admin${route.path}`
      : location.pathname.startsWith(`/admin${route.path}`);
  }

  function isParentRouteActive(route) {
    return route.children?.some((child) =>
      location.pathname.startsWith(`/admin${child.path}`),
    );
  }
}
